import React from 'react';
import { AlertTriangle, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// No stylesheet — converted to Tailwind utilities in Phase 5. OverLimitBanner.scss
// was deleted in the same commit (the atomic-migration rule, TOKEN_CONTRACT §6.1).
// Its two severities were hardcoded amber and red with hand-picked dark inks; they
// are the warning and danger token families now, so the banner is finally readable
// in dark mode — it never was.

/**
 * Persistent banner shown on entity list pages when the institute
 * is at or over their plan limit for that resource.
 *
 * Props:
 *   resourceName  — Human label, e.g. "Students"
 *   used          — Current count
 *   max           — Plan limit (0 = unlimited)
 *   overLimit     — true if used >= max
 *   strictlyOver  — true if used > max (editing also blocked)
 */

const TONE = {
  warning: {
    box: 'tw-border-warning/40 tw-bg-warning-soft tw-text-warning-fg',
    icon: 'tw-text-warning',
    action: 'tw-bg-warning tw-text-on-status hover:tw-bg-warning-fg',
  },
  danger: {
    box: 'tw-border-danger/40 tw-bg-danger-soft tw-text-danger-fg',
    icon: 'tw-text-danger',
    action: 'tw-bg-danger tw-text-on-status hover:tw-bg-danger-fg',
  },
};

const OverLimitBanner = ({ resourceName, used, max, overLimit, strictlyOver }) => {
  const navigate = useNavigate();

  if (!overLimit || max === 0) return null;

  const tone = TONE[strictlyOver ? 'danger' : 'warning'];
  const noun = resourceName.toLowerCase();

  return (
    <div
      className={`tw-mb-4 tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-leading-snug sm:tw-flex-row sm:tw-gap-3 sm:tw-text-left ${tone.box}`}
    >
      <div className={`tw-flex tw-shrink-0 tw-items-center [&_svg]:tw-stroke-[2.5] ${tone.icon}`}>
        <AlertTriangle size={18} />
      </div>
      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-0.5">
        <span className="tw-text-sm tw-font-semibold">
          {strictlyOver
            ? `Over plan limit — ${used}/${max} ${resourceName}`
            : `Plan limit reached — ${used}/${max} ${resourceName}`}
        </span>
        <span className="tw-text-[13px] tw-opacity-85">
          {strictlyOver
            ? `You have ${used - max} more ${noun} than your plan allows. You cannot add or edit ${noun} until you upgrade or remove excess entries.`
            : `You've reached the maximum ${noun} for your plan. You cannot add new ${noun} — upgrade to increase your limit.`}
        </span>
      </div>
      <button
        type="button"
        className={`tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-md tw-border-0 tw-px-3.5 tw-py-1.5 tw-text-[13px] tw-font-semibold tw-transition-colors ${tone.action}`}
        onClick={() => navigate('/billing')}
      >
        Upgrade <ArrowUpRight size={14} />
      </button>
    </div>
  );
};

export default OverLimitBanner;
